import { Save, Settings, Trash2, X } from "lucide-react";
import { memo, useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useCreateQuestion,
  useRemoveQuestion,
  useUpdateQuestion,
} from "@/services/assessments/question";

// Tipo local para questões no formulário
interface LocalQuestion {
  id: string;
  description: string;
}

// Tipo para questões que podem vir do service ou ser locais
interface FormQuestion extends LocalQuestion {
  isLocal?: boolean;
}

interface QuestionItemProps {
  index: number;
  isNew?: boolean;
  isSelected: boolean;
  assessmentId: string;
  question: FormQuestion;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onUpdate: (id: string, newValues: Partial<FormQuestion>) => void;
}

function QuestionItemComponent({
  question,
  index,
  isSelected,
  onSelect,
  onUpdate,
  onDelete,
  assessmentId,
  isNew = false,
}: QuestionItemProps) {
  const [isEditing, setIsEditing] = useState(isNew);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [description, setDescription] = useState(question.description || "");

  const createQuestion = useCreateQuestion();
  const updateQuestion = useUpdateQuestion();
  const removeQuestion = useRemoveQuestion();

  useEffect(() => {
    setDescription(question.description || "");
  }, [question.description]);

  const handleSave = async () => {
    if (!description.trim()) {
      toast.error("A descrição da questão é obrigatória");
      return;
    }

    setIsSubmitting(true);
    try {
      if (isNew) {
        // Criar nova questão
        const result = await createQuestion.mutateAsync({
          assessmentId, // Será preenchido pelo backend
          description: description.trim(),
        });

        onUpdate(question.id, {
          ...question,
          description: description.trim(),
          id: result.question.id, // Atualizar com o ID real do backend
          isLocal: false, // Marcar como não local após salvar
        });
        toast.success("Questão criada com sucesso!");
      } else {
        // Atualizar questão existente
        await updateQuestion.mutateAsync({
          id: question.id,
          description: description.trim(),
        });

        onUpdate(question.id, {
          ...question,
          description: description.trim(),
        });
        toast.success("Questão atualizada com sucesso!");
      }

      setIsEditing(false);
    } catch (error) {
      toast.error("Erro ao salvar questão");
      console.error("Erro ao salvar questão:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (isNew) {
      // Se é nova, apenas remover do estado local
      onDelete(question.id);
      return;
    }

    try {
      await removeQuestion.mutateAsync(question.id);
      onDelete(question.id);
      toast.success("Questão removida com sucesso!");
    } catch (error) {
      toast.error("Erro ao remover questão");
      console.error("Erro ao remover questão:", error);
    }
  };

  const handleCancel = () => {
    if (isNew) {
      // Se é nova e cancelar, remover a questão
      onDelete(question.id);
    } else {
      // Restaurar descrição original e sair do modo edição
      setDescription(question.description || "");
      setIsEditing(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSelect = () => {
    if (!isEditing) {
      onSelect(question.id);
    }
  };

  return (
    <div
      className={`w-full text-left px-3 py-2 rounded border border-disabled transition ${
        isSelected
          ? isEditing
            ? "border-primary bg-transparent text-white"
            : "bg-primary text-white"
          : "hover:border-white"
      }`}
      onClick={handleSelect}
    >
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1 flex-1">
          <div className="flex items-center gap-2">
            <div className="text-sm bg-disabled min-w-5 min-h-5 rounded-sm flex items-center justify-center flex-shrink-0 font-normal">
              {index + 1}
            </div>
            <span className="font-medium">Questão {index + 1}</span>
          </div>

          {isEditing ? (
            <div className="flex items-center gap-2 mt-2">
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Digite a descrição da questão..."
                className="flex-1"
                disabled={isSubmitting}
              />
            </div>
          ) : (
            <span className="text-xs text-gray-500 line-clamp-1 cursor-pointer">
              {question.description || "Sem descrição"}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 ml-2">
          {isEditing ? (
            <>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleSave}
                disabled={isSubmitting}
                className="p-1 h-auto"
              >
                <Save className="size-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="p-1 h-auto"
              >
                <X className="size-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleEdit}
                className="p-1 h-auto"
              >
                <Settings className="size-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleDelete}
                disabled={isSubmitting}
                className="p-1 h-auto"
              >
                <Trash2 className="size-4" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Usar React.memo para evitar re-renders desnecessários
export const QuestionItem = memo(QuestionItemComponent);
